// Ogg page writer (RFC 3533) with the CRC-32 Ogg uses: polynomial 0x04C11DB7,
// initial value 0, no bit reflection, no final xor — hand-rolled because it is
// NOT the zlib CRC and no platform API provides it.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let bit = 0; bit < 8; bit++) {
      r = (r & 0x80000000) !== 0 ? ((r << 1) ^ 0x04c11db7) >>> 0 : (r << 1) >>> 0;
    }
    table[i] = r >>> 0;
  }
  return table;
})();

export function oggCrc32(bytes: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < bytes.length; i++) {
    crc = (((crc << 8) >>> 0) ^ CRC_TABLE[((crc >>> 24) ^ bytes[i]!) & 0xff]!) >>> 0;
  }
  return crc >>> 0;
}

const MAX_PAGE_PAYLOAD = 255 * 255;

export class OggPageWriter {
  private readonly pages: Uint8Array[] = [];
  private pageSequence = 0;

  constructor(private readonly serial: number) {}

  /**
   * Writes one packet as one page (lacing values 255…, r). Fine for headers and
   * for Opus audio packets, which stay far under the 65 025-byte page payload
   * cap; anything larger is a malformed capture, not a case to paper over.
   */
  addPacketPage(packet: Uint8Array, options: { granule: number; bos?: boolean; eos?: boolean }): void {
    if (packet.length >= MAX_PAGE_PAYLOAD) {
      throw new RangeError(`packet of ${packet.length} B does not fit one Ogg page`);
    }
    const lacing: number[] = [];
    let remaining = packet.length;
    while (remaining >= 255) {
      lacing.push(255);
      remaining -= 255;
    }
    lacing.push(remaining);

    const header = new Uint8Array(27 + lacing.length);
    const view = new DataView(header.buffer);
    header[0] = 0x4f; // 'O'
    header[1] = 0x67; // 'g'
    header[2] = 0x67; // 'g'
    header[3] = 0x53; // 'S'
    header[4] = 0; // version
    header[5] = (options.bos === true ? 0x02 : 0) | (options.eos === true ? 0x04 : 0);
    // Granule position, 64-bit little-endian. Values stay far below 2^53.
    view.setUint32(6, options.granule >>> 0, true);
    view.setUint32(10, Math.floor(options.granule / 2 ** 32), true);
    view.setUint32(14, this.serial >>> 0, true);
    view.setUint32(18, this.pageSequence++, true);
    view.setUint32(22, 0, true); // CRC placeholder
    header[26] = lacing.length;
    header.set(lacing, 27);

    const page = new Uint8Array(header.length + packet.length);
    page.set(header, 0);
    page.set(packet, header.length);
    const crc = oggCrc32(page);
    new DataView(page.buffer).setUint32(22, crc, true);
    this.pages.push(page);
  }

  finish(): Uint8Array {
    const total = this.pages.reduce((sum, p) => sum + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const page of this.pages) {
      out.set(page, offset);
      offset += page.length;
    }
    return out;
  }
}
